import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchableSelect from './shared/SearchableSelect';
import { Icon } from './shared/Icon';
import { hasEffectivePermission } from './shared/constants';

// Maldives phone helpers for one-tap call / WhatsApp in chase mode.
const phoneDigits = (p) => (p || '').replace(/\D/g, '');
const waHref = (p) => { let d = phoneDigits(p); if (d.length === 7) d = '960' + d; return `https://wa.me/${d}`; };
const fmtCountdown = (ms) => {
  if (ms == null) return '';
  if (ms <= 0) return 'closed';
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s}s`;
};
const toLocalInput = (iso) => {
  const d = new Date(iso); const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function GOTVDashboard({ supabase, user, allLocations = [], patchAgents = [], allVoters: votersFromProps = [] }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [voters, setVoters]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState(location.state?.filter || 'All');
  const [committedIds, setCommittedIds] = useState(() => new Set());
  const [searchText, setSearchText]   = useState('');
  const [volunteers, setVolunteers]   = useState([]);
  const [showVolPanel, setShowVolPanel] = useState(false);
  const [newVolName, setNewVolName]   = useState('');
  const [newVolPhone, setNewVolPhone] = useState('');
  const [newVolAtoll, setNewVolAtoll] = useState('');
  const [newVolIsland, setNewVolIsland] = useState('');
  const [newVolAgentId, setNewVolAgentId] = useState('');
  const [volunteerFilter, setVolunteerFilter] = useState('All');
  const [atollFilter, setAtollFilter]   = useState('All');
  const [islandFilter, setIslandFilter] = useState('All');
  const [assigningVoterId, setAssigningVoterId] = useState(null);
  const [transportTypeFilter, setTransportTypeFilter] = useState('All');
  const [undoingIds, setUndoingIds]   = useState(() => new Set());
  const [transportPending, setTransportPending] = useState(() => new Set());
  const [expandedIslands, setExpandedIslands] = useState(() => new Set());
  const toggleIslandSection = (key) => setExpandedIslands(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  // Election-day additions
  const [now, setNow] = useState(Date.now());            // ticks for the countdown
  const [gotvCfg, setGotvCfg] = useState(null);          // app_settings 'gotv' { poll_close_at }
  const [editingClose, setEditingClose] = useState(false);
  const [chaseMode, setChaseMode] = useState(false);     // phone-first focused mode
  const [groupMode, setGroupMode] = useState('island');  // 'island' | 'box'
  const [round, setRound] = useState(1);                 // 1 | 2 (active election round)
  const [chaseIsland, setChaseIsland] = useState('All'); // chase-mode scoping
  const [chaseBox, setChaseBox] = useState('All');
  const [chaseMine, setChaseMine] = useState(false);
  const [chaseStatus, setChaseStatus] = useState({});    // voter_id -> 'No answer' | 'On the way' | 'Refused'
  const canManageGotv = hasEffectivePermission(user, 'canManageUsers');
  const votedField = round === 2 ? 'voted_r2' : 'voted_2028';
  const boxField   = round === 2 ? 'ballot_box_r2' : 'ballot_box_r1';
  // "My voters": gotv_assigned_to may be this user's id (UUID) or their name.
  const isMine = (v) => v.gotv_assigned_to && (String(v.gotv_assigned_to) === String(user?.id) || v.gotv_assigned_to === user?.name || v.gotv_assigned_to === user?.full_name);

  // Tick the clock for the live countdown (every 30s is enough for h/m display).
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Load poll-close config once.
  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'gotv').maybeSingle()
      .then(({ data, error }) => {
        if (error) console.error('[app_settings] gotv config load failed:', error);
        if (data?.value) setGotvCfg(data.value);
      });
  }, [supabase]);

  // Live board: merge other runners' voted/transport/assignment changes in real time.
  // Requires the voters table in the supabase_realtime publication (see 1_SETUP.sql).
  useEffect(() => {
    const ch = supabase.channel('gotv-rt')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'voters' }, payload => {
        const r = payload.new; if (!r) return;
        setVoters(vs => vs.map(v => v.id === r.id
          ? { ...v, voted_2028: r.voted_2028, voted_r2: r.voted_r2, voted_at: r.voted_at, picked_up: r.picked_up, transport_type: r.transport_type, needs_transport: r.needs_transport, gotv_assigned_to: r.gotv_assigned_to }
          : v));
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [supabase]);

  const savePollClose = async (iso) => {
    const value = { ...(gotvCfg || {}), poll_close_at: iso || null };
    setGotvCfg(value);
    setEditingClose(false);
    await supabase.from('app_settings').upsert({ key: 'gotv', value });
  };

  // Chase status (side table — avoids the voters_secured view).
  useEffect(() => {
    supabase.from('gotv_chase').select('voter_id, status').then(({ data, error }) => {
      if (error) console.error('[gotv_chase] load failed:', error);
      const m = {}; for (const r of (data || [])) m[r.voter_id] = r.status; setChaseStatus(m);
    });
  }, [supabase]);
  useEffect(() => {
    const ch = supabase.channel('gotv-chase-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'gotv_chase' }, payload => {
        if (payload.eventType === 'DELETE') {
          const id = payload.old?.voter_id; if (id != null) setChaseStatus(prev => { const n = { ...prev }; delete n[id]; return n; });
        } else if (payload.new) {
          setChaseStatus(prev => ({ ...prev, [payload.new.voter_id]: payload.new.status }));
        }
      }).subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [supabase]);
  const setVoterChase = async (voterId, status) => {
    const next = chaseStatus[voterId] === status ? null : status;
    setChaseStatus(prev => ({ ...prev, [voterId]: next }));
    if (next === null) await supabase.from('gotv_chase').delete().eq('voter_id', voterId);
    else await supabase.from('gotv_chase').upsert({ voter_id: voterId, status: next, updated_by: user?.id || null, updated_at: new Date().toISOString() }, { onConflict: 'voter_id' });
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [votersFromProps.length]);


  const loadData = async () => {
    // Voters come from the prop (already loaded in App.jsx) — no extra query.
    // Commitment is "active" only if the latest commitment-related outreach is 'Commitment Made'.
    const [{ data: commitments, error: commitmentsErr }, { data: vols, error: volsErr }] = await Promise.all([
      supabase.from('outreach').select('voter_id, outcome, created_at')
        .in('outcome', ['Commitment Made', 'Commitment Withdrawn'])
        .order('created_at', { ascending: false }),
      supabase.from('gotv_volunteers').select('*').order('name'),
    ]);
    if (commitmentsErr) console.error('[gotv] outreach load failed:', commitmentsErr);
    if (volsErr) console.error('[gotv] volunteers load failed:', volsErr);

    const seen = new Set();
    const committedSet = new Set();
    for (const row of (commitments || [])) {
      const k = String(row.voter_id);
      if (seen.has(k)) continue;
      seen.add(k);
      if (row.outcome === 'Commitment Made') committedSet.add(k);
    }
    setCommittedIds(committedSet);

    // Filter voters from props: supporters + any committed (regardless of support level)
    const filtered = (votersFromProps || []).filter(v =>
      v.support === 'Strong Support' || v.support === 'Leaning Support' || committedSet.has(String(v.id))
    );

    // IMPORTANT: props (allVoters) are loaded once per session, so they hold the
    // page-load state. On the periodic 60s refresh, preserve the day's mutable
    // fields (voted/transport/assignment) from local + realtime so marks aren't
    // reverted. First load (prev empty) just takes props.
    setVoters(prev => {
      if (prev.length === 0) return filtered;
      const local = new Map(prev.map(v => [v.id, v]));
      return filtered.map(v => {
        const l = local.get(v.id);
        return l ? { ...v, voted_2028: l.voted_2028, voted_r2: l.voted_r2, voted_at: l.voted_at, picked_up: l.picked_up, transport_type: l.transport_type, needs_transport: l.needs_transport, gotv_assigned_to: l.gotv_assigned_to } : v;
      });
    });
    setVolunteers(vols || []);
    setLoading(false);
    setUndoingIds(new Set());
  };

  const markVoted = async (voterId) => {
    const at = new Date().toISOString();
    setVoters(vs => vs.map(v => v.id === voterId ? { ...v, [votedField]: true, voted_at: at } : v));
    const { error } = await supabase.from('voters').update({ [votedField]: true, voted_at: at }).eq('id', voterId);
    if (error) setVoters(vs => vs.map(v => v.id === voterId ? { ...v, [votedField]: false } : v));
  };

  const undoVoted = async (voterId) => {
    setUndoingIds(s => new Set([...s, voterId]));
    setVoters(vs => vs.map(v => v.id === voterId ? { ...v, [votedField]: false } : v));
    const { error } = await supabase.from('voters').update({ [votedField]: false }).eq('id', voterId);
    if (error) setVoters(vs => vs.map(v => v.id === voterId ? { ...v, [votedField]: true } : v));
    setUndoingIds(s => { const n = new Set(s); n.delete(voterId); return n; });
  };

  const setTransport = async (voterId, type) => {
    setTransportPending(s => new Set([...s, voterId]));
    const prev = voters.find(v => v.id === voterId);
    setVoters(vs => vs.map(v => v.id === voterId ? { ...v, transport_type: type, needs_transport: !!type } : v));
    const { error } = await supabase.from('voters')
      .update({ transport_type: type || null, needs_transport: !!type })
      .eq('id', voterId);
    if (error) setVoters(vs => vs.map(v => v.id === voterId ? { ...v, transport_type: prev?.transport_type, needs_transport: prev?.needs_transport } : v));
    setTransportPending(s => { const n = new Set(s); n.delete(voterId); return n; });
  };

  const togglePickedUp = async (voterId, val) => {
    setVoters(vs => vs.map(v => v.id === voterId ? { ...v, picked_up: val } : v));
    const { error } = await supabase.from('voters').update({ picked_up: val }).eq('id', voterId);
    if (error) setVoters(vs => vs.map(v => v.id === voterId ? { ...v, picked_up: !val } : v));
  };

  const addVolunteer = async () => {
    if (!newVolName.trim()) return;
    const { error } = await supabase.from('gotv_volunteers').insert({
      name: newVolName.trim(),
      phone: newVolPhone.trim() || null,
      atoll: newVolAtoll || null,
      island: newVolIsland || null,
    });
    if (error) { alert('Could not add volunteer: ' + error.message); return; }
    setNewVolName(''); setNewVolPhone(''); setNewVolAtoll(''); setNewVolIsland(''); setNewVolAgentId('');
    const { data } = await supabase.from('gotv_volunteers').select('*').order('name');
    setVolunteers(data || []);
  };

  const removeVolunteer = async (vol) => {
    if (!window.confirm(`Remove ${vol.name}? Their voter assignments will be cleared.`)) return;
    await Promise.all([
      supabase.from('gotv_volunteers').delete().eq('id', vol.id),
      supabase.from('voters').update({ gotv_assigned_to: null }).eq('gotv_assigned_to', vol.name),
    ]);
    if (volunteerFilter === vol.name) setVolunteerFilter('All');
    loadData();
  };

  const assignVolunteer = async (voterId, volunteerName) => {
    const val = volunteerName || null;
    setVoters(vs => vs.map(v => v.id === voterId ? { ...v, gotv_assigned_to: val } : v));
    await supabase.from('voters').update({ gotv_assigned_to: val }).eq('id', voterId);
    setAssigningVoterId(null);
  };

  // ── Stats ───────────────────────────────────────────────────
  // Stats always reflect the selected atoll/island
  const islandVotersForStats = voters
    .filter(v => atollFilter === 'All' || v.atoll === atollFilter)
    .filter(v => islandFilter === 'All' || v.island === islandFilter);
  const totalSupporters = islandVotersForStats.length;
  const assignedCount   = islandVotersForStats.filter(v => v.gotv_assigned_to).length;
  const committed       = islandVotersForStats.filter(v => committedIds.has(String(v.id))).length;
  const voted           = islandVotersForStats.filter(v => v[votedField]).length;
  const notVoted        = islandVotersForStats.length - voted;
  const needsTransport  = islandVotersForStats.filter(v => v.transport_type && !v[votedField]).length;
  const turnoutRate     = islandVotersForStats.length > 0 ? (voted / islandVotersForStats.length) * 100 : 0;
  const priorityVoters  = islandVotersForStats.filter(v => committedIds.has(String(v.id)) && !v[votedField]);

  // ── Countdown / pace ────────────────────────────────────────
  const pollCloseAt = gotvCfg?.poll_close_at ? new Date(gotvCfg.poll_close_at).getTime() : null;
  const msLeft      = pollCloseAt != null ? pollCloseAt - now : null;
  const hoursLeft   = msLeft != null ? msLeft / 3600000 : null;
  const pollsClosed = msLeft != null && msLeft <= 0;
  const paceNeeded  = (hoursLeft != null && hoursLeft > 0 && notVoted > 0) ? Math.ceil(notVoted / hoursLeft) : 0;
  // Actual momentum: how many were marked voted in the last hour.
  const votedLastHour = islandVotersForStats.filter(v => v[votedField] && v.voted_at && (now - new Date(v.voted_at).getTime()) <= 3600000).length;
  const onTrack = paceNeeded > 0 ? votedLastHour >= paceNeeded : true;

  // Turnout-over-the-day (from voted_at, today only) + final projection + win number
  const _now = new Date(now);
  const isToday = (ts) => { const d = new Date(ts); return d.getFullYear() === _now.getFullYear() && d.getMonth() === _now.getMonth() && d.getDate() === _now.getDate(); };
  const hourly = (() => {
    const b = {};
    for (const v of islandVotersForStats) {
      if (!v[votedField] || !v.voted_at || !isToday(v.voted_at)) continue;
      const h = new Date(v.voted_at).getHours();
      b[h] = (b[h] || 0) + 1;
    }
    return b;
  })();
  const hourKeys = Object.keys(hourly).map(Number).sort((a, b) => a - b);
  const projectedFinal = (pollCloseAt && !pollsClosed && hoursLeft > 0)
    ? Math.min(voted + Math.round(votedLastHour * hoursLeft), islandVotersForStats.length) : null;
  const electorate = (votersFromProps || []).length;
  const winNumber = Math.ceil(electorate / 2);
  // urgency tint: red if <1h or pace very high, amber if <3h, else green
  const urgency = pollsClosed ? 'closed' : (hoursLeft == null ? null : hoursLeft < 1 ? 'red' : hoursLeft < 3 ? 'amber' : 'green');
  const URG = { red: '#ef4444', amber: '#f59e0b', green: '#10b981', closed: 'var(--text-3)' };

  // ── Location lists (from locations table, merged with voter data) ──
  const atolls = [...new Set([
    ...allLocations.map(l => l.atoll),
    ...voters.map(v => v.atoll).filter(Boolean),
  ])].sort();
  const islandsForAtoll = atollFilter === 'All'
    ? [...new Set([
        ...allLocations.map(l => l.island),
        ...voters.map(v => v.island).filter(Boolean),
      ])].sort()
    : [...new Set([
        ...allLocations.filter(l => l.atoll === atollFilter).map(l => l.island),
        ...voters.filter(v => v.atoll === atollFilter).map(v => v.island).filter(Boolean),
      ])].sort();

  // Islands for the volunteer add form (based on newVolAtoll)
  const volFormIslands = newVolAtoll
    ? [...new Set([
        ...allLocations.filter(l => l.atoll === newVolAtoll).map(l => l.island),
        ...voters.filter(v => v.atoll === newVolAtoll).map(v => v.island).filter(Boolean),
      ])].sort()
    : [...new Set([
        ...allLocations.map(l => l.island),
        ...voters.map(v => v.island).filter(Boolean),
      ])].sort();

  // ── Filter ──────────────────────────────────────────────────
  // Atoll → Island applied first; all counts and tabs reflect the selection.
  const locationVoters = voters
    .filter(v => atollFilter === 'All' || v.atoll === atollFilter)
    .filter(v => islandFilter === 'All' || v.island === islandFilter);

  const islandVoters = locationVoters;

  const islandPriorityVoters = islandVoters.filter(v => committedIds.has(String(v.id)) && !v[votedField]);

  // Chase mode: unvoted supporters, committed → strong → leaning, then island/name.
  // Respects location + own scoping filters (island / ballot box / my voters) + search.
  const chaseRank = (v) => committedIds.has(String(v.id)) ? 0 : v.support === 'Strong Support' ? 1 : 2;
  const chasePool = islandVoters.filter(v => !v[votedField]);
  const chaseIslandOpts = [...new Set(chasePool.map(v => v.island).filter(Boolean))].sort();
  const chaseBoxOpts = [...new Set(chasePool.map(v => v[boxField]).filter(Boolean))].sort();
  const chaseList = chasePool
    .filter(v => chaseIsland === 'All' || v.island === chaseIsland)
    .filter(v => chaseBox === 'All' || (v[boxField] || '') === chaseBox)
    .filter(v => !chaseMine || isMine(v))
    .filter(v => {
      if (!searchText.trim()) return true;
      const q = searchText.trim().toLowerCase();
      return [v.name, v.phone, v.phone2, v.island, v.ward].filter(Boolean).join(' ').toLowerCase().includes(q);
    })
    .sort((a, b) => chaseRank(a) - chaseRank(b) || (a.island || '').localeCompare(b.island || '') || (a.name || '').localeCompare(b.name || ''));

  let displayVoters = islandVoters;
  if (filter === 'Priority')       displayVoters = islandPriorityVoters;
  else if (filter === 'NotVoted')  displayVoters = islandVoters.filter(v => !v[votedField]);
  else if (filter === 'Voted')     displayVoters = islandVoters.filter(v => v[votedField]);
  else if (filter === 'Transport') displayVoters = islandVoters.filter(v => v.transport_type && !v[votedField]);

  if (volunteerFilter !== 'All') {
    if (volunteerFilter === 'Unassigned') {
      displayVoters = displayVoters.filter(v => !v.gotv_assigned_to);
    } else {
      const linkedAgent = patchAgents.find(a => a.full_name === volunteerFilter);
      displayVoters = displayVoters.filter(v =>
        v.gotv_assigned_to === volunteerFilter ||
        (linkedAgent && v.gotv_assigned_to === linkedAgent.id)
      );
    }
  }

  if (filter === 'Transport' && transportTypeFilter !== 'All') {
    displayVoters = displayVoters.filter(v => v.transport_type === transportTypeFilter);
  }

  if (searchText.trim()) {
    const q = searchText.trim().toLowerCase();
    displayVoters = displayVoters.filter(v =>
      [v.name, v.phone, v.phone2, v.island, v.ward].filter(Boolean).join(' ').toLowerCase().includes(q)
    );
  }

  const isVoted = (v) => v[votedField] && !undoingIds.has(v.id);

  const filterTabs = [
    { key: 'All',       label: 'All',            count: islandVotersForStats.length },
    { key: 'Priority',  label: 'Priority',       count: priorityVoters.length },
    { key: 'NotVoted',  label: 'Not Voted',      count: notVoted },
    { key: 'Voted',     label: 'Voted',          count: voted },
    { key: 'Transport', label: 'Need Transport', count: needsTransport },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-2)' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid var(--border-2)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        Loading GOTV dashboard...
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      {/* ── Poll-close countdown + pace ───────────────────────── */}
      {(pollCloseAt || canManageGotv) && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          background: 'var(--surface)', boxShadow: 'var(--shadow-sm)', borderRadius: 14, padding: '14px 18px', marginBottom: 16,
          border: `1px solid ${pollCloseAt && urgency && urgency !== 'closed' ? URG[urgency] : 'var(--border)'}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ width: 40, height: 40, borderRadius: 11, display: 'grid', placeItems: 'center', flexShrink: 0,
              background: (pollCloseAt ? URG[urgency] : '#3b82f6') + '1f', color: pollCloseAt ? URG[urgency] : '#3b82f6' }}>
              <Icon name="clock" size={20} />
            </span>
            {pollCloseAt ? (pollsClosed ? (
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: URG.closed, lineHeight: 1 }}>Polls closed</div>
            ) : (
              <div>
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--text-3)' }}>
                  Polls close {new Date(pollCloseAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: URG[urgency], lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtCountdown(msLeft)} left
                </div>
              </div>
            )) : (
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Set the poll-close time to start the countdown.</div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            {pollCloseAt && !pollsClosed && (
              <>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: '#f59e0b', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{notVoted}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-3)', marginTop: 3 }}>still to vote</div>
                </div>
                {paceNeeded > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: URG[urgency], lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>~{paceNeeded}</div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-3)', marginTop: 3 }}>per hour</div>
                  </div>
                )}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: onTrack ? '#10b981' : '#ef4444', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{votedLastHour}</div>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-3)', marginTop: 3 }}>last hour</div>
                </div>
                {paceNeeded > 0 && (
                  <span style={{ alignSelf: 'center', padding: '5px 11px', borderRadius: 8, fontSize: 11, fontWeight: 700, fontFamily: "'DM Mono', monospace", background: (onTrack ? '#10b981' : '#ef4444') + '1f', color: onTrack ? '#10b981' : '#ef4444', whiteSpace: 'nowrap' }}>
                    {onTrack ? 'On track' : 'Behind'}
                  </span>
                )}
              </>
            )}
            {canManageGotv && (
              <button onClick={() => setEditingClose(e => !e)} title="Set poll-close time"
                style={{ width: 34, height: 34, borderRadius: 9, display: 'grid', placeItems: 'center', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', flexShrink: 0 }}>
                <Icon name="settings" size={15} />
              </button>
            )}
          </div>

          {editingClose && canManageGotv && (
            <div style={{ flexBasis: '100%', display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              <input type="datetime-local"
                defaultValue={gotvCfg?.poll_close_at ? toLocalInput(gotvCfg.poll_close_at) : ''}
                onChange={e => savePollClose(e.target.value ? new Date(e.target.value).toISOString() : null)}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 13 }} />
              {gotvCfg?.poll_close_at && (
                <button onClick={() => savePollClose(null)} style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>Clear</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Mode toggle: Dashboard / Chase · Round selector ───── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['dash', 'Dashboard', 'chart'], ['chase', 'Chase Mode', 'target']].map(([key, label, icon]) => {
          const on = (key === 'chase') === chaseMode;
          return (
            <button key={key} onClick={() => setChaseMode(key === 'chase')} style={{
              padding: '9px 16px', borderRadius: 10,
              border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
              background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
              color: on ? '#60a5fa' : 'var(--text-2)', fontSize: 13, fontWeight: 600,
              fontFamily: "'DM Mono', monospace", cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
              <Icon name={icon} size={15} />{label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)' }}>Round</span>
        <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
          {[1, 2].map(r => {
            const on = round === r;
            return (
              <button key={r} onClick={() => setRound(r)} style={{
                padding: '8px 14px', border: 'none',
                background: on ? 'rgba(59,130,246,0.18)' : 'transparent',
                color: on ? '#60a5fa' : 'var(--text-2)', fontSize: 12.5, fontWeight: 600,
                fontFamily: "'DM Mono', monospace", cursor: 'pointer',
              }}>R{r}</button>
            );
          })}
        </div>
      </div>

      {/* ── CHASE MODE ────────────────────────────────────────── */}
      {chaseMode ? (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={searchText} onChange={e => setSearchText(e.target.value)} placeholder="Search name or phone…"
              style={{ flex: 1, minWidth: 180, padding: '11px 13px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 15, outline: 'none' }} />
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: 'var(--text-3)' }}>{chaseList.length} to chase</span>
          </div>
          {/* Chase scoping: island · ballot box · my voters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ minWidth: 150, flex: '1 1 150px' }}>
              <SearchableSelect value={chaseIsland === 'All' ? '' : chaseIsland} options={chaseIslandOpts}
                onChange={v => setChaseIsland(v || 'All')} placeholder="All islands" />
            </div>
            <div style={{ minWidth: 150, flex: '1 1 150px' }}>
              <SearchableSelect value={chaseBox === 'All' ? '' : chaseBox} options={chaseBoxOpts}
                onChange={v => setChaseBox(v || 'All')} placeholder={`All boxes · R${round}`} />
            </div>
            <button onClick={() => setChaseMine(m => !m)} style={{
              padding: '9px 14px', borderRadius: 10, cursor: 'pointer', whiteSpace: 'nowrap',
              border: chaseMine ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
              background: chaseMine ? 'rgba(59,130,246,0.13)' : 'transparent',
              color: chaseMine ? '#60a5fa' : 'var(--text-2)', fontSize: 12.5, fontWeight: 600,
              fontFamily: "'DM Mono', monospace", display: 'inline-flex', alignItems: 'center', gap: 7,
            }}>
              <Icon name="user" size={14} />My voters
            </button>
            {(chaseIsland !== 'All' || chaseBox !== 'All' || chaseMine) && (
              <button onClick={() => { setChaseIsland('All'); setChaseBox('All'); setChaseMine(false); }}
                style={{ padding: '9px 12px', borderRadius: 10, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Clear
              </button>
            )}
          </div>
          {chaseList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--text-3)' }}>
              <div style={{ display: 'grid', placeItems: 'center', marginBottom: 12 }}><Icon name="check" size={40} color="#10b981" /></div>
              <p style={{ fontSize: 15 }}>Everyone here has voted.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {chaseList.map(v => {
                const committed = committedIds.has(String(v.id));
                const box = v[boxField];
                return (
                  <div key={v.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${committed ? '#10b981' : '#3b82f6'}`, borderRadius: 12, padding: '14px 16px', boxShadow: 'var(--shadow-sm)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div onClick={() => navigate(`/voter/${v.id}`)} style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-strong)', cursor: 'pointer', marginBottom: 3 }}>{v.name}</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', fontSize: 12, color: 'var(--text-3)' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 8, fontWeight: 600, background: committed ? '#10b98120' : '#3b82f620', color: committed ? '#10b981' : '#60a5fa' }}>
                          {committed ? 'Committed' : v.support === 'Strong Support' ? 'Strong' : 'Leaning'}
                        </span>
                        <span>{v.island}{v.ward ? ` · ${v.ward}` : ''}</span>
                        {box && <span>· Box {box}</span>}
                        {v.phone && <span>· {v.phone}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        {[['No answer', '#f59e0b'], ['On the way', '#3b82f6'], ['Refused', '#ef4444']].map(([st, col]) => {
                          const on = chaseStatus[v.id] === st;
                          return (
                            <button key={st} onClick={() => setVoterChase(v.id, st)} title="Mark chase status (tap again to clear)" style={{
                              padding: '3px 9px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                              fontFamily: "'DM Mono', monospace",
                              border: `1px solid ${on ? col : 'var(--border)'}`,
                              background: on ? col + '22' : 'transparent',
                              color: on ? col : 'var(--text-3)',
                            }}>{st}</button>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {v.phone && <a href={`tel:${v.phone}`} title="Call" style={{ width: 44, height: 44, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'var(--surface-2)', border: '1px solid var(--border)', color: '#60a5fa', textDecoration: 'none' }}><Icon name="phone" size={18} /></a>}
                      {v.phone && <a href={waHref(v.phone)} target="_blank" rel="noreferrer" title="WhatsApp" style={{ width: 44, height: 44, borderRadius: 10, display: 'grid', placeItems: 'center', background: 'var(--surface-2)', border: '1px solid var(--border)', color: '#22c55e', textDecoration: 'none' }}><Icon name="message" size={18} /></a>}
                      <button onClick={() => markVoted(v.id)} style={{ minWidth: 44, height: 44, padding: '0 16px', borderRadius: 10, border: 'none', background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="check" size={16} color="#fff" />Voted
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
      <>
      {/* ── Header stats ──────────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#10b9811f', color: '#10b981', flexShrink: 0 }}>
              <Icon name="vote" size={20} />
            </span>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Get Out The Vote</div>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>Election Day Dashboard</h2>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ minWidth: 140, flex: 1 }}>
              <SearchableSelect
                value={atollFilter === 'All' ? '' : atollFilter}
                options={atolls}
                onChange={v => { setAtollFilter(v || 'All'); setIslandFilter('All'); }}
                placeholder="All Atolls"
              />
            </div>
            <div style={{ minWidth: 140, flex: 1 }}>
              <SearchableSelect
                value={islandFilter === 'All' ? '' : islandFilter}
                options={islandsForAtoll}
                onChange={v => setIslandFilter(v || 'All')}
                placeholder="All Islands"
              />
            </div>
            {(atollFilter !== 'All' || islandFilter !== 'All') && (
              <button onClick={() => { setAtollFilter('All'); setIslandFilter('All'); }}
                style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Clear
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
          {[
            { label: 'Total Supporters', value: totalSupporters, color: '#3b82f6', tab: 'All',       icon: 'users' },
            { label: 'Voted',            value: voted,           color: '#10b981', tab: 'Voted',     icon: 'check' },
            { label: 'Not Yet Voted',    value: notVoted,        color: '#f59e0b', tab: 'NotVoted',  icon: 'clock' },
            { label: 'Turnout Rate',     value: `${Math.round(turnoutRate)}%`, color: '#8b5cf6', tab: null,    icon: 'trending' },
            { label: 'Need Transport',   value: needsTransport,  color: '#06b6d4', tab: 'Transport', icon: 'car' },
          ].map(({ label, value, color, tab, icon }) => {
            const on = tab && filter === tab;
            return (
              <div key={label} onClick={() => tab && setFilter(tab)} style={{
                background: 'var(--surface-2)',
                padding: '14px 16px', borderRadius: '12px',
                cursor: tab ? 'pointer' : 'default',
                border: on ? `1px solid ${color}` : '1px solid var(--border)',
                boxShadow: on ? `0 0 0 1px ${color}` : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.15s',
                minHeight: 96, position: 'relative', overflow: 'hidden',
              }}
                onMouseEnter={e => { if (tab) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = color; } }}
                onMouseLeave={e => { if (tab) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = on ? color : 'var(--border)'; } }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-2)', fontWeight: 500, lineHeight: 1.2 }}>{label}</span>
                  <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: color + '1f', color, flexShrink: 0 }}>
                    <Icon name={icon} size={15} />
                  </span>
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color, lineHeight: 1, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.5px' }}>{value}</div>
                {tab && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: color, opacity: on ? 1 : 0, transition: 'opacity 0.15s' }} />}
              </div>
            );
          })}
        </div>

        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
            <span style={{ color: 'var(--text-2)' }}>
              Supporter Turnout
              {atollFilter !== 'All' && ` · ${atollFilter}`}
              {islandFilter !== 'All' && ` · ${islandFilter}`}
            </span>
            <span style={{ fontWeight: '600' }}>{voted} / {islandVotersForStats.length}</span>
          </div>
          <div style={{ height: '24px', background: 'var(--surface-2)', borderRadius: '12px', overflow: 'hidden', position: 'relative', border: '1px solid var(--border)' }}>
            <div style={{ height: '100%', width: `${turnoutRate}%`, background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '12px', transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)' }} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: '12px', fontWeight: '700', color: 'white', fontVariantNumeric: 'tabular-nums', textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}>
              {Math.round(turnoutRate)}%
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {filterTabs.map(({ key, label, count }) => {
            const on = filter === key;
            return (
              <button key={key} onClick={() => { setFilter(key); if (key !== 'Transport') setTransportTypeFilter('All'); }} style={{
                padding: '8px 14px', borderRadius: '9px',
                border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                color: on ? '#60a5fa' : 'var(--text-2)',
                fontSize: '12.5px', fontWeight: '600', cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                display: 'inline-flex', alignItems: 'center', gap: 7,
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
                onMouseEnter={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; } }}
                onMouseLeave={e => { if (!on) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; } }}
              >{label}
                <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 8, background: on ? 'rgba(59,130,246,0.25)' : 'var(--surface-2)', color: on ? '#93c5fd' : 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
              </button>
            );
          })}
        </div>

        {filter === 'Transport' && (() => {
          const toCollect = islandVoters.filter(v => v.transport_type && !v[votedField] && !v.picked_up).length;
          const collected = islandVoters.filter(v => v.transport_type && !v[votedField] && v.picked_up).length;
          return (
          <div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
            <span style={{ color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 1, fontSize: 10 }}>Dispatch</span>
            <span style={{ color: '#f59e0b', fontVariantNumeric: 'tabular-nums' }}>● {toCollect} to collect</span>
            <span style={{ color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>● {collected} en route / collected</span>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            {[
              { key: 'All',  label: 'All',  icon: null },
              { key: 'Land', label: 'Land', icon: 'car' },
              { key: 'Sea',  label: 'Sea',  icon: 'ship' },
              { key: 'Air',  label: 'Air',  icon: 'plane' },
            ].map(({ key, icon }) => {
              const cnt = key === 'All'
                ? islandVoters.filter(v => v.transport_type && !v[votedField]).length
                : islandVoters.filter(v => v.transport_type === key && !v[votedField]).length;
              const on = transportTypeFilter === key;
              return (
                <button key={key} onClick={() => setTransportTypeFilter(key)} style={{
                  padding: '6px 13px', borderRadius: '9px', cursor: 'pointer',
                  border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                  background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                  color: on ? '#60a5fa' : 'var(--text-2)',
                  fontSize: '12.5px', fontWeight: '600',
                  fontFamily: "'DM Mono', monospace",
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                }}>
                  {icon && <Icon name={icon} size={14} />}{key}
                  <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 8, background: on ? 'rgba(59,130,246,0.25)' : 'var(--surface-2)', color: on ? '#93c5fd' : 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{cnt}</span>
                </button>
              );
            })}
          </div>
          </div>
          );
        })()}

        <input type="text" value={searchText} onChange={e => setSearchText(e.target.value)}
          placeholder="Search by name or phone…"
          style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '6px', border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: '14px', outline: 'none' }} />
      </div>

      {/* ── Turnout trend + projection + win number ───────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6' }}><Icon name="trending" size={15} /></span>
            Turnout Trend
          </h3>
          <div style={{ display: 'flex', gap: 16, fontFamily: "'DM Mono', monospace", fontSize: 11.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {winNumber > 0 && <span style={{ color: 'var(--text-3)' }}>Win number <span style={{ color: 'var(--text)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>~{winNumber.toLocaleString()}</span></span>}
            {projectedFinal != null && (
              <span style={{ color: 'var(--text-3)' }}>Projected <span style={{ color: onTrack ? '#10b981' : '#f59e0b', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>~{projectedFinal.toLocaleString()}</span> ({Math.round(projectedFinal / Math.max(islandVotersForStats.length, 1) * 100)}%) by close</span>
            )}
          </div>
        </div>
        {hourKeys.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-4)', padding: '8px 0' }}>No votes recorded yet today.</div>
        ) : (() => {
          const maxC = Math.max(...hourKeys.map(h => hourly[h]));
          const fmtH = (h) => { const ap = h < 12 ? 'a' : 'p'; const hh = h % 12 === 0 ? 12 : h % 12; return `${hh}${ap}`; };
          return (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 92, paddingTop: 8 }}>
              {hourKeys.map(h => (
                <div key={h} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                  <span style={{ fontSize: 10, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', fontFamily: "'DM Mono', monospace" }}>{hourly[h]}</span>
                  <div title={`${hourly[h]} voted at ${fmtH(h)}`} style={{ width: '100%', maxWidth: 28, height: `${Math.max(6, (hourly[h] / maxC) * 56)}px`, background: 'linear-gradient(180deg, #34d399, #10b981)', borderRadius: 4 }} />
                  <span style={{ fontSize: 9.5, color: 'var(--text-4)', fontFamily: "'DM Mono', monospace" }}>{fmtH(h)}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* ── Volunteers Panel ──────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '16px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowVolPanel(v => !v)}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6', flexShrink: 0 }}><Icon name="users" size={15} /></span>
            GOTV Volunteers
            <span style={{ fontSize: '12px', color: 'var(--text-3)', fontWeight: '400', marginLeft: 4 }}>
              {volunteers.length} volunteer{volunteers.length !== 1 ? 's' : ''} · {assignedCount} voters assigned
            </span>
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {volunteerFilter !== 'All' && (
              <button onClick={e => { e.stopPropagation(); setVolunteerFilter('All'); }}
                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                Reset Filter
              </button>
            )}
            <span style={{ color: 'var(--text-3)', fontSize: '14px', userSelect: 'none' }}>{showVolPanel ? '▲' : '▼'}</span>
          </div>
        </div>

        {showVolPanel && (
          <div style={{ marginTop: 16 }}>
            {/* Today's leaders — ranked by votes pulled among their assigned */}
            {volunteers.length > 0 && (() => {
              const ranked = volunteers.map(vol => {
                const linkedAgent = patchAgents.find(a => a.full_name === vol.name);
                const assigned = voters.filter(v => v.gotv_assigned_to === vol.name || (linkedAgent && v.gotv_assigned_to === linkedAgent.id));
                return { name: vol.name, voted: assigned.filter(v => v[votedField]).length, total: assigned.length };
              }).filter(r => r.total > 0).sort((a, b) => b.voted - a.voted).slice(0, 5);
              if (ranked.length === 0) return null;
              const medal = ['#fbbf24', '#cbd5e1', '#d97706'];
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 8 }}>Today's leaders · votes pulled</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {ranked.map((r, i) => (
                      <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                        <span style={{ width: 18, textAlign: 'center', fontWeight: 700, fontSize: 12, color: i < 3 ? medal[i] : 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                        <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{r.name}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>{r.voted}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>/ {r.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            {/* Volunteer cards */}
            {volunteers.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 16 }}>
                {volunteers.map(vol => {
                  // If volunteer name matches a patch agent, also count voters assigned by ID (team assignment)
                  const linkedAgent = patchAgents.find(a => a.full_name === vol.name);
                  const assigned   = voters.filter(v =>
                    v.gotv_assigned_to === vol.name ||
                    (linkedAgent && v.gotv_assigned_to === linkedAgent.id)
                  );
                  const votedCount = assigned.filter(v => v[votedField]).length;
                  const isActive   = volunteerFilter === vol.name;
                  const selectVol = () => { const next = isActive ? 'All' : vol.name; setVolunteerFilter(next); if (next !== 'All') setFilter('All'); };
                  return (
                    <div key={vol.id}
                      onClick={selectVol}
                      style={{
                        background: isActive ? '#0d2137' : 'var(--surface-2)',
                        border: `1px solid ${isActive ? '#3b82f6' : 'var(--border)'}`,
                        borderRadius: 8, padding: '12px 14px',
                        cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
                      }}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor = '#3b82f6'; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor = 'var(--border)'; }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{vol.name}</div>
                          {vol.island && (
                            <div style={{ fontSize: 11, color: 'var(--link)', marginTop: 2 }}>📍 {vol.island}{vol.atoll ? `, ${vol.atoll}` : ''}</div>
                          )}
                          {vol.phone && (
                            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>📞 {vol.phone}</div>
                          )}
                        </div>
                        <button onClick={e => { e.stopPropagation(); removeVolunteer(vol); }}
                          style={{ background: 'none', border: 'none', color: 'var(--border-2)', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>✕</button>
                      </div>

                      <div style={{ marginTop: 8, fontSize: 12 }}>
                        <span style={{ color: 'var(--text-2)' }}>{assigned.length} assigned</span>
                        {assigned.length > 0 && (
                          <>
                            <span style={{ color: 'var(--border-2)', margin: '0 4px' }}>·</span>
                            <span style={{ color: '#10b981' }}>{votedCount} voted</span>
                            <span style={{ color: 'var(--text-3)' }}> ({Math.round(votedCount / assigned.length * 100)}%)</span>
                          </>
                        )}
                      </div>

                      {assigned.length > 0 && (
                        <div style={{ marginTop: 6, height: 4, background: 'var(--border)', borderRadius: 2 }}>
                          <div style={{ height: '100%', width: `${votedCount / assigned.length * 100}%`, background: '#10b981', borderRadius: 2, transition: 'width 0.4s' }} />
                        </div>
                      )}

                      {isActive && (
                        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--link)', fontWeight: 600 }}>✓ Filtering by this volunteer</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Unassigned quick filter */}
            {assignedCount < totalSupporters && (
              <button onClick={() => { const next = volunteerFilter === 'Unassigned' ? 'All' : 'Unassigned'; setVolunteerFilter(next); if (next !== 'All') setFilter('All'); }}
                style={{
                  marginBottom: 12, padding: '6px 14px', borderRadius: 6,
                  border: `1px solid ${volunteerFilter === 'Unassigned' ? '#f59e0b' : 'var(--border-2)'}`,
                  background: volunteerFilter === 'Unassigned' ? '#1a120000' : 'transparent',
                  color: volunteerFilter === 'Unassigned' ? '#f59e0b' : 'var(--text-3)',
                  cursor: 'pointer', fontSize: 12,
                }}>
                ⚠ {totalSupporters - assignedCount} voters unassigned
              </button>
            )}

            {/* Add volunteer form */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                value={newVolAgentId}
                onChange={e => {
                  const id = e.target.value;
                  setNewVolAgentId(id);
                  if (id) {
                    const agent = patchAgents.find(a => a.id === id);
                    if (agent) {
                      setNewVolName(agent.full_name);
                      if (agent.phone) setNewVolPhone(agent.phone);
                    }
                  }
                }}
                style={{ flex: '1 1 160px', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: newVolAgentId ? 'var(--link)' : 'var(--text-3)', fontSize: 13, outline: 'none' }}
              >
                <option value="">{patchAgents.length ? 'From team (optional)' : 'No patch agents'}</option>
                {patchAgents.map(a => (
                  <option key={a.id} value={a.id}>{a.full_name}</option>
                ))}
              </select>
              <input value={newVolName} onChange={e => { setNewVolName(e.target.value); setNewVolAgentId(''); }}
                onKeyDown={e => e.key === 'Enter' && addVolunteer()}
                placeholder="Volunteer name"
                style={{ flex: '1 1 140px', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 13, outline: 'none' }} />
              <input value={newVolPhone} onChange={e => setNewVolPhone(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addVolunteer()}
                placeholder="Phone (optional)"
                style={{ flex: '1 1 120px', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-2)', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 13, outline: 'none' }} />
              <div style={{ flex: '1 1 130px' }}>
                <SearchableSelect
                  value={newVolAtoll}
                  options={atolls}
                  onChange={v => { setNewVolAtoll(v); setNewVolIsland(''); }}
                  placeholder="Atoll"
                />
              </div>
              <div style={{ flex: '1 1 130px' }}>
                <SearchableSelect
                  value={newVolIsland}
                  options={volFormIslands}
                  onChange={v => setNewVolIsland(v)}
                  placeholder="Island"
                />
              </div>
              <button onClick={addVolunteer}
                style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#3b82f6', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
                + Add
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Voter Checklist ───────────────────────────────────── */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', borderRadius: '14px', padding: '24px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px' }}>
          {filter === 'Priority'  && '⚠️ Priority — Committed but Not Voted'}
          {filter === 'NotVoted'  && '📋 All Not Yet Voted'}
          {filter === 'Voted'     && '🗳️ Confirmed Voted'}
          {filter === 'Transport' && '🚗🚤✈️ Needs Transport'}
          {filter === 'All'       && '👥 All Supporters'}
          {volunteerFilter !== 'All' && (
            <span style={{ fontSize: '13px', fontWeight: 400, color: '#3b82f6', marginLeft: 8 }}>
              · {volunteerFilter === 'Unassigned' ? 'Unassigned' : `${volunteerFilter}'s voters`}
              <button onClick={() => setVolunteerFilter('All')}
                style={{ marginLeft: 6, background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>×</button>
            </span>
          )}
          {searchText.trim() && (
            <span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--text-2)', marginLeft: 8 }}>
              — {displayVoters.length} result{displayVoters.length !== 1 ? 's' : ''}
            </span>
          )}
        </h3>

        {/* Group-by toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-4)' }}>Group by</span>
          {[['island', 'Island'], ['box', 'Ballot box']].map(([key, label]) => {
            const on = groupMode === key;
            return (
              <button key={key} onClick={() => setGroupMode(key)} style={{
                padding: '6px 12px', borderRadius: 8,
                border: on ? '1px solid rgba(59,130,246,0.55)' : '1px solid var(--border)',
                background: on ? 'rgba(59,130,246,0.13)' : 'transparent',
                color: on ? '#60a5fa' : 'var(--text-2)', fontSize: 12, fontWeight: 600,
                fontFamily: "'DM Mono', monospace", cursor: 'pointer',
              }}>{label}</button>
            );
          })}
          {groupMode === 'box' && (
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: 'var(--text-4)', marginLeft: 2 }}>· Round {round}</span>
          )}
        </div>

        {(() => {
          // Group displayed voters by island OR ballot box (active round's box).
          const NOKEY = '__none__';
          const keyOf = (v) => groupMode === 'box'
            ? (v[boxField] || NOKEY)
            : (v.island || NOKEY);
          const grouped = {};
          for (const v of displayVoters) {
            const k = keyOf(v);
            if (!grouped[k]) grouped[k] = [];
            grouped[k].push(v);
          }
          const namedKeys = Object.keys(grouped).filter(k => k !== NOKEY).sort();
          const sections = [...namedKeys, ...(grouped[NOKEY] ? [NOKEY] : [])];
          const overallPct = displayVoters.length ? displayVoters.filter(v => isVoted(v)).length / displayVoters.length * 100 : 0;

          const renderVoterCard = (voter) => {
            const voted2028 = isVoted(voter);
            const isAssigning = assigningVoterId === voter.id;
            return (
              <div key={voter.id} style={{
                background: voted2028 ? '#0f141920' : 'var(--surface-2)',
                borderRadius: '8px', padding: '16px',
                borderLeft: `4px solid ${voted2028 ? 'var(--text-3)' : voter.transport_type ? '#06b6d4' : '#10b981'}`,
                opacity: voted2028 ? 0.75 : 1,
                transition: 'opacity 0.2s',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    {/* Name */}
                    <h4 onClick={() => navigate(`/voter/${voter.id}`)}
                      style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      onMouseEnter={e => e.currentTarget.style.color = 'var(--link)'}
                      onMouseLeave={e => e.currentTarget.style.color = ''}>
                      {voted2028 && <Icon name="check" size={14} color="#10b981" />}{voter.name}
                      <span style={{ fontSize: '10px', color: '#3b82f6', opacity: 0.7 }}>↗</span>
                    </h4>

                    <div style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '8px' }}>
                      📍 {voter.island}{voter.ward ? `, ${voter.ward}` : ''}
                      {voter.phone && ` · 📞 ${voter.phone}`}
                    </div>

                    {/* Badges row */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {committedIds.has(voter.id) && (
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#10b98120', color: '#10b981' }}>Committed</span>
                      )}

                      {voter.transport_type ? (
                        <>
                        <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: '#06b6d420', color: '#06b6d4', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Icon name={voter.transport_type === 'Land' ? 'car' : voter.transport_type === 'Sea' ? 'ship' : 'plane'} size={13} />
                          {voter.transport_type}
                          {!voted2028 && (
                            <button onClick={() => setTransport(voter.id, null)} disabled={transportPending.has(voter.id)}
                              style={{ background: 'transparent', border: 'none', color: '#06b6d4', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '0 2px', opacity: transportPending.has(voter.id) ? 0.4 : 1 }}>×</button>
                          )}
                        </span>
                        {!voted2028 && (
                          <button onClick={() => togglePickedUp(voter.id, !voter.picked_up)}
                            title={voter.picked_up ? 'Picked up / en route — tap to undo' : 'Mark as picked up for transport'}
                            style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                              border: voter.picked_up ? '1px solid #10b981' : '1px dashed var(--border-2)',
                              background: voter.picked_up ? '#10b98120' : 'transparent',
                              color: voter.picked_up ? '#10b981' : 'var(--text-2)',
                              display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Icon name={voter.picked_up ? 'check' : 'car'} size={13} />{voter.picked_up ? 'Picked up' : 'Pick up'}
                          </button>
                        )}
                        </>
                      ) : !voted2028 && (
                        <span style={{ display: 'inline-flex', gap: 4, opacity: transportPending.has(voter.id) ? 0.4 : 1 }}>
                          {[['Land', 'car'], ['Sea', 'ship'], ['Air', 'plane']].map(([type, icon]) => (
                            <button key={type} onClick={() => setTransport(voter.id, type)} disabled={transportPending.has(voter.id)}
                              title={type}
                              style={{ padding: '4px 9px', borderRadius: '12px', background: 'transparent', border: '1px dashed var(--border-2)', cursor: 'pointer', color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center' }}>
                              <Icon name={icon} size={14} />
                            </button>
                          ))}
                        </span>
                      )}

                      {/* Volunteer assignment badge.
                          gotv_assigned_to can be a volunteer name (string) or a patch agent's
                          profile ID (UUID). Resolve UUID → patch agent's full_name. If the value
                          is a UUID with no matching agent (e.g. agent was deleted), treat as
                          orphaned — show as unassigned instead of leaking a raw UUID. */}
                      {(() => {
                        const raw = voter.gotv_assigned_to;
                        if (!raw || isAssigning) return null;
                        const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                        const looksLikeUuid = uuidRe.test(String(raw));
                        const matchedAgent = looksLikeUuid ? patchAgents.find(a => a.id === raw) : null;
                        const displayName = matchedAgent ? matchedAgent.full_name : (looksLikeUuid ? null : raw);
                        if (!displayName) {
                          // Orphaned UUID — show unassign control instead of the raw value
                          return !voted2028 ? (
                            <button onClick={() => setAssigningVoterId(voter.id)}
                              style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: 'transparent', border: '1px dashed var(--text-4)', color: 'var(--text-2)', cursor: 'pointer' }}>
                              + Assign volunteer
                            </button>
                          ) : null;
                        }
                        return (
                          <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: 'var(--border)', color: 'var(--link)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <Icon name="user" size={12} /> {displayName}
                            {!voted2028 && (
                              <button onClick={() => setAssigningVoterId(voter.id)}
                                style={{ background: 'transparent', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: '10px', padding: '0 2px' }}>✎</button>
                            )}
                          </span>
                        );
                      })()}
                      {!voter.gotv_assigned_to && !voted2028 && !isAssigning && (
                        <button onClick={() => setAssigningVoterId(voter.id)}
                          style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', background: 'transparent', border: '1px dashed var(--border)', color: '#4b6a8a', cursor: 'pointer' }}>
                          + Assign
                        </button>
                      )}
                    </div>

                    {/* Inline assignment dropdown */}
                    {isAssigning && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select
                          defaultValue={voter.gotv_assigned_to || ''}
                          onChange={e => assignVolunteer(voter.id, e.target.value)}
                          autoFocus
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #3b82f6', background: 'var(--surface-2)', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }}>
                          <option value=''>— Unassign —</option>
                          {voter.island && volunteers.some(v => v.island === voter.island) && (
                            <optgroup label={`📍 ${voter.island}`}>
                              {volunteers.filter(v => v.island === voter.island).map(v => (
                                <option key={v.id} value={v.name}>{v.name}</option>
                              ))}
                            </optgroup>
                          )}
                          {volunteers.filter(v => !voter.island || v.island !== voter.island).length > 0 && (
                            <optgroup label="Other islands">
                              {volunteers.filter(v => !voter.island || v.island !== voter.island).map(v => (
                                <option key={v.id} value={v.name}>{v.name}{v.island ? ` · ${v.island}` : ''}</option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        <button onClick={() => setAssigningVoterId(null)}
                          style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                        {volunteers.length === 0 && (
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>Add volunteers in the panel above first</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
                    {!voted2028 ? (
                      <button onClick={() => markVoted(voter.id)}
                        style={{ padding: '10px 20px', borderRadius: '8px', border: 'none', background: '#10b981', color: 'white', fontSize: '14px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        ✓ Mark Voted
                      </button>
                    ) : (
                      <button onClick={() => undoVoted(voter.id)}
                        style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        title="Undo — mark as not voted">
                        ↩ Undo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          };

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sections.map(key => {
                const isNone = key === '__none__';
                const isCollapsed = !expandedIslands.has(key);
                const headerText = isNone
                  ? (groupMode === 'box' ? 'No ballot box set' : 'Unspecified island')
                  : (groupMode === 'box' ? `Box ${key}` : key);
                const items = grouped[key];
                const votedHere = items.filter(v => isVoted(v)).length;
                const remaining = items.length - votedHere;
                const pct = items.length ? votedHere / items.length * 100 : 0;
                const lagging = !isNone && items.length >= 3 && pct < overallPct - 10;
                const headColor = isNone ? '#ef4444' : lagging ? '#f59e0b' : 'var(--link)';
                return (
                  <div key={key}>
                    <div onClick={() => toggleIslandSection(key)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isCollapsed ? 0 : 10, cursor: 'pointer', userSelect: 'none', flexWrap: 'wrap' }}>
                      <Icon name={groupMode === 'box' ? 'vote' : 'mapPin'} size={13} color={headColor} />
                      <span style={{ fontSize: 12, fontWeight: 700, color: headColor, textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: "'DM Mono', monospace" }}>
                        {headerText}
                      </span>
                      {lagging && <span style={{ fontSize: 9, fontWeight: 700, color: '#f59e0b', border: '1px solid #f59e0b', borderRadius: 5, padding: '1px 6px', fontFamily: "'DM Mono', monospace" }}>LAGGING</span>}
                      <div style={{ flex: 1, height: 1, background: 'var(--border)', minWidth: 20 }} />
                      <span style={{ fontSize: 11, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{Math.round(pct)}%</span>
                      <span style={{ fontSize: 11, color: '#10b981', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>✓ {votedHere}</span>
                      <span style={{ fontSize: 11, color: '#f59e0b', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{remaining} left</span>
                      <span style={{ fontSize: 11, color: 'var(--text-4)', marginLeft: 4 }}>{isCollapsed ? '▸' : '▾'}</span>
                    </div>
                    {!isCollapsed && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {items.map(renderVoterCard)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {displayVoters.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-3)' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {searchText ? '🔎' : filter === 'Priority' || filter === 'NotVoted' ? '🎉' : '👥'}
            </div>
            <p>
              {searchText ? 'No voters match that search.'
                : filter === 'Priority' || filter === 'NotVoted' ? 'All done!'
                : 'No voters in this group.'}
            </p>
          </div>
        )}
      </div>
      </>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
